<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary" icon="account_circle" aria-label="Profile menu" title="Profile menu">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <VaAvatar :size="32" :color="selectedAvatarBackground.value">
              <span class="profile-dropdown__avatar-emoji">{{ selectedAvatar.value }}</span>
            </VaAvatar>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList>
          <header class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">RepoDepot</header>
          <div class="px-4 pt-1 pb-2">
            <p class="profile-dropdown__username m-0 text-sm font-semibold leading-5">
              {{ displayUserName }}
            </p>
            <p v-if="displayUserEmail" class="profile-dropdown__email m-0 text-xs text-[var(--va-secondary)] truncate">
              {{ displayUserEmail }}
            </p>
            <p class="profile-dropdown__role m-0 text-[11px] uppercase tracking-wide opacity-70">
              {{ displayRole }}
            </p>
          </div>
          <VaDivider class="my-2" />
          <RouterLink :to="{ name: 'profile' }" class="no-underline">
            <VaListItem class="menu-item px-4 text-base h-8 cursor-pointer hover:bg-background-element">
              <VaIcon name="settings" class="pr-1" color="secondary" />
              Profile
            </VaListItem>
          </RouterLink>
          <VaDivider class="my-2" />
          <VaListItem class="menu-item px-4 text-base h-8 cursor-pointer text-danger" @click="handleLogout">
            <VaIcon name="logout" class="pr-1" color="danger" />
            Logout
          </VaListItem>
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, RouterLink } from 'vue-router'
import { useColors } from 'vuestic-ui'
import { fetchPanelProfile, isAdminAuthenticated, clearAdminAuthState, type PanelProfile } from '../../../../services/repodepot-api'
import { useUserStore } from '../../../../stores/user-store'

const { colors, setHSLAColor } = useColors()
const { push } = useRouter()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))
const userStore = useUserStore()

const isShown = ref(false)
const profile = ref<PanelProfile | null>(null)
const selectedAvatar = computed(() => userStore.selectedProfileAvatar)
const selectedAvatarBackground = computed(() => userStore.selectedProfileAvatarBackground)

const displayUserName = computed(() => {
  const resolvedName = String(userStore.userName ?? '').trim()
  if (resolvedName && resolvedName !== 'Loading...') {
    return resolvedName
  }

  const fallbackEmail = String(userStore.email ?? '').trim()
  if (fallbackEmail) {
    return fallbackEmail.includes('@') ? fallbackEmail.split('@')[0] : fallbackEmail
  }

  return String(profile.value?.display_name ?? 'User').trim() || 'User'
})

const displayUserEmail = computed(() => String(userStore.email ?? '').trim() || '')

const displayRole = computed(() => {
  if (profile.value?.is_configured_admin) {
    return 'System Admin'
  }

  return String(profile.value?.role ?? 'User').trim() || 'User'
})

async function loadProfile() {
  await userStore.initialize()

  if (!isAdminAuthenticated()) {
    profile.value = null
    return
  }

  try {
    profile.value = await fetchPanelProfile()
    userStore.applyProfileAvatarFromUser(profile.value)
  } catch {
    profile.value = null
  }
}

const handleLogout = () => {
  clearAdminAuthState()
  isShown.value = false
  void push({ name: 'login' })
}

onMounted(() => {
  void loadProfile()
})
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }

  &__avatar-emoji {
    font-size: 1.1rem;
    line-height: 1;
  }

  &__username,
  &__email,
  &__role {
    max-width: 100%;
  }
}
</style>

